/* @refresh reset */
import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";
import { IBudget, IBudgetVersionService, IBudgetVersionServiceItem } from "../models/budgetV2";

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  header: {
    marginBottom: 12,
  },
  budgetInfo: {
    fontSize: 11,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 8,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  twoColumns: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 12,
  },
  column: {
    flex: 1,
  },
  bold: {
    fontWeight: "bold",
  },
  table: {
    marginTop: 8,
    marginBottom: 8,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    padding: 8,
    borderBottom: "1px solid #d1d5db",
  },
  tableRow: {
    flexDirection: "row",
    padding: 6,
    borderBottom: "1px solid #e5e7eb",
    minHeight: 20,
  },
  tableCell: {
    fontSize: 9,
  },
  colRef: { width: "7%" },
  colItem: { width: "13%" },
  colDesc: { width: "24%" },
  colQty: { width: "9%", textAlign: "right" },
  colValue: { width: "10%", textAlign: "right" },
  colCobertura: { width: "12%" },
  colDiscount: { width: "9%", textAlign: "right" },
  colSubtotal: { width: "16%", textAlign: "right" },
  summary: {
    marginTop: 12,
    padding: 10,
    backgroundColor: "#f9fafb",
    border: "1px solid #d1d5db",
    borderRadius: 4,
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 4,
    gap: 12,
  },
  infoBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    paddingBottom: 10,
    borderBottom: "1px solid #e5e7eb",
  },
  infoBarItem: {
    fontSize: 8,
    color: "#6b7280",
    marginBottom: 2,
  },
  infoBarValue: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#000000",
  },
  statusBadgePending: {
    backgroundColor: "#9ca3af",
    color: "white",
    padding: "4px 8px",
    borderRadius: 9999,
    fontSize: 9,
    fontWeight: "bold",
  },
  statusBadgeApproved: {
    backgroundColor: "#9ca3af",
    color: "white",
    padding: "4px 8px",
    borderRadius: 9999,
    fontSize: 9,
    fontWeight: "bold",
  },
  statusBadgeRejected: {
    backgroundColor: "#9ca3af",
    color: "white",
    padding: "4px 8px",
    borderRadius: 9999,
    fontSize: 9,
    fontWeight: "bold",
  },
  vehicleCard: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
});

const getServiceItems = (service: IBudgetVersionService): IBudgetVersionServiceItem[] => {
  return service.serviceItems ?? service.items ?? [];
};

const BudgetPdfDocumentV2 = ({ data }: { data: IBudget }) => {
  const formatCurrency = (value?: number): string => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value ?? 0);
  };

  const formatDateOnly = (iso?: string): string => {
    if (!iso) return "";
    let isoString = iso.trim();
    if (!isoString.endsWith("Z") && !isoString.match(/[+-]\d{2}:\d{2}$/)) {
      isoString = `${isoString}Z`;
    }
    const d = new Date(isoString);
    return d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
  };

  const versionData = data.versionData;
  const statusLabel = versionData?.statusLabel ?? data.statusLabel ?? data.currentUserStatus ?? "Pendente";
  const recipientLabel = versionData?.recipientLabel ?? data.recipientLabel ?? "";
  const consultantName = data.consultantName ?? "";
  const versionNumber = versionData?.versionNumber ?? data.lastVersionNumber;
  const isCounterProposalAccepted = Boolean(versionData?.counterProposalApprovedAt);
  const dateLabel = isCounterProposalAccepted
    ? formatDateOnly(versionData?.counterProposalApprovedAt)
    : formatDateOnly(versionData?.updatedAt ?? data.updatedAt ?? new Date().toISOString());

  const getStatusBadgeStyle = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes("aprovado")) return styles.statusBadgeApproved;
    if (statusLower.includes("recusado") || statusLower.includes("rejeitado")) return styles.statusBadgeRejected;
    return styles.statusBadgePending;
  };

  const services = versionData?.services ?? [];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.budgetInfo}>
            <Text style={styles.bold}>OCOR: {data.occurrenceProtocolNumber || ""}</Text>
          </Text>
          <Text style={styles.budgetInfo}>
            <Text style={styles.bold}>Orçamento #{data.number || ""}</Text>
          </Text>
        </View>

        <View style={styles.twoColumns}>
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>Veículo</Text>
            <View style={{ border: "1px solid #e5e7eb", borderRadius: 4, padding: 12 }}>
              <View style={{ backgroundColor: "#f9fafb", borderRadius: 4, padding: 12 }}>
                <View style={styles.vehicleCard}>
                  <View style={{ flex: 1 }}>
                    {data.vehicleModel && (
                      <Text style={[styles.bold, { marginBottom: 4, fontSize: 11 }]}>
                        {data.vehicleModel}
                        {data.vehicleLicensePlate && ` | Placa: ${data.vehicleLicensePlate}`}
                      </Text>
                    )}
                    {data.vehicleChassis && (
                      <Text style={{ marginBottom: 4, fontSize: 10 }}>Chassi - {data.vehicleChassis}</Text>
                    )}
                    {(data.vehicleOdometer || data.vehicleHourMeter) && (
                      <Text style={{ marginBottom: 4, fontSize: 10 }}>
                        {data.vehicleOdometer && `Odômetro: ${data.vehicleOdometer}`}
                        {data.vehicleOdometer && data.vehicleHourMeter && " | "}
                        {data.vehicleHourMeter && `Horímetro: ${data.vehicleHourMeter}`}
                      </Text>
                    )}
                    {data.vehicleContractNumber && (
                      <Text style={[styles.bold, { fontSize: 10 }]}>
                        Volks total Prev: Contrato #{data.vehicleContractNumber}
                      </Text>
                    )}
                  </View>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.column}>
            <Text style={styles.sectionTitle}>Cliente</Text>
            <View style={{ border: "1px solid #e5e7eb", padding: 12, borderRadius: 4 }}>
              {data.customerName && (
                <Text style={[styles.bold, { marginBottom: 8 }]}>{data.customerName}</Text>
              )}
              <View style={styles.row}>
                <Text>{data.customerPhone || ""}</Text>
                <Text>{data.customerEmail || ""}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.infoBar}>
          <View>
            <Text style={styles.infoBarItem}>Consultor da concessionária</Text>
            <Text style={styles.infoBarValue}>{consultantName || "-"}</Text>
          </View>
          <View>
            <Text style={styles.infoBarItem}>Destinatário</Text>
            <Text style={styles.infoBarValue}>{recipientLabel || "-"}</Text>
          </View>
          <View>
            <Text style={styles.infoBarItem}>Versão</Text>
            <Text style={styles.infoBarValue}>{versionNumber ? versionNumber.toFixed(2) : "1.00"}</Text>
          </View>
          <View>
            <Text style={styles.infoBarItem}>Data</Text>
            <Text style={styles.infoBarValue}>{dateLabel}</Text>
          </View>
          <View>
            <View style={getStatusBadgeStyle(statusLabel)}>
              <Text>{statusLabel}</Text>
            </View>
          </View>
        </View>

        <View style={{ marginBottom: 12 }}>
          <Text style={[styles.sectionTitle, { marginBottom: 10, fontSize: 13 }]}>Valores</Text>
          {services.map((service, serviceIndex) => {
            const serviceItems = getServiceItems(service);
            const visibleItems = serviceItems.filter(
              (item) => item.removed !== true && (item.quantity ?? 0) > 0
            );
            const serviceName = service.name ?? service.service ?? "Serviço";

            return (
              <View
                key={service.id || `service-${serviceIndex}`}
                style={{ marginBottom: 12, border: "1px solid #e5e7eb", padding: 10, borderRadius: 4 }}
              >
                <View style={{ backgroundColor: "#f3f4f6", padding: 6, borderRadius: 4, marginBottom: 8 }}>
                  <Text style={[styles.bold, { fontSize: 11 }]}>{serviceName}</Text>
                </View>

                <View style={styles.table}>
                  <View style={styles.tableHeader}>
                    <Text style={[styles.tableCell, styles.colRef, styles.bold]}>Ref.</Text>
                    <Text style={[styles.tableCell, styles.colItem, styles.bold]}>Item</Text>
                    <Text style={[styles.tableCell, styles.colDesc, styles.bold]}>Descrição</Text>
                    <Text style={[styles.tableCell, styles.colQty, styles.bold]}>Quantidade</Text>
                    <Text style={[styles.tableCell, styles.colValue, styles.bold]}>Valor (R$)</Text>
                    <Text style={[styles.tableCell, styles.colCobertura, styles.bold]}>Cobertura</Text>
                    <Text style={[styles.tableCell, styles.colDiscount, styles.bold]}>% De desconto</Text>
                    <Text style={[styles.tableCell, styles.colSubtotal, styles.bold]}>Subtotal (R$)</Text>
                  </View>
                  {visibleItems.map((item, itemIndex) => {
                    const quantity = item.quantity ?? 0;
                    const discountPercent = item.discount ?? 0;
                    const isCovered = item.isCovered === true;
                    const itemSubtotal = isCovered ? 0 : quantity * (item.value ?? 0) * (1 - discountPercent / 100);
                    const isRemoved = item.removed === true;

                    return (
                      <View
                        key={`item-${item.ref}-${itemIndex}`}
                        style={[styles.tableRow, isRemoved ? { backgroundColor: "#f9fafb" } : {}]}
                      >
                        <Text style={[styles.tableCell, styles.colRef]}>{item.ref || ""}</Text>
                        <Text style={[styles.tableCell, styles.colItem]}>{item.item || ""}</Text>
                        <Text style={[styles.tableCell, styles.colDesc]}>{item.description || ""}</Text>
                        <Text style={[styles.tableCell, styles.colQty]}>{quantity || ""}</Text>
                        <Text style={[styles.tableCell, styles.colValue]}>{formatCurrency(item.value)}</Text>
                        <Text style={[styles.tableCell, styles.colCobertura]}>
                          {isCovered ? "Coberto pelo plano" : "Sem cobertura"}
                        </Text>
                        <Text style={[styles.tableCell, styles.colDiscount]}>{discountPercent}%</Text>
                        <Text style={[styles.tableCell, styles.colSubtotal]}>{formatCurrency(itemSubtotal)}</Text>
                      </View>
                    );
                  })}
                </View>

                {visibleItems.length > 0 && (
                  <View style={{ marginTop: 6, alignItems: "flex-end" }}>
                    <Text style={{ fontSize: 9, color: "#000000" }}>
                      Subtotal do serviço:{" "}
                      <Text style={styles.bold}>
                        {formatCurrency(
                          visibleItems.reduce((sum, item) => {
                            if (item.isCovered === true) return sum;
                            const itemSubtotal = (item.quantity ?? 0) * (item.value ?? 0) * (1 - (item.discount ?? 0) / 100);
                            return sum + itemSubtotal;
                          }, 0)
                        )}
                      </Text>
                    </Text>
                  </View>
                )}
              </View>
            );
          })}
        </View>

        <View style={styles.summary}>
          <View style={styles.summaryRow}>
            <Text style={{ color: "#6b7280" }}>Subtotal:</Text>
            <Text style={styles.bold}>{formatCurrency(versionData?.subtotal)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={{ color: "#000000" }}>Descontos:</Text>
            <Text style={styles.bold}>{formatCurrency(versionData?.discount)}</Text>
          </View>
          {isCounterProposalAccepted && (
            <View style={[styles.summaryRow, { textDecoration: "line-through", opacity: 0.5 }]}>
              <Text style={[styles.bold, { fontSize: 12 }]}>Valor a ser pago:</Text>
              <Text style={[styles.bold, { fontSize: 12 }]}>{formatCurrency(versionData?.netTotal)}</Text>
            </View>
          )}
          <View style={styles.summaryRow}>
            <Text style={[styles.bold, { fontSize: 12 }]}>Valor a ser pago:</Text>
            <Text style={[styles.bold, { fontSize: 12 }]}>
              {formatCurrency(isCounterProposalAccepted ? versionData?.counterProposalValue : versionData?.netTotal)}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export const downloadBudgetPdfV2 = async (budgetData: IBudget) => {
  try {
    const doc = <BudgetPdfDocumentV2 data={budgetData} />;
    const asPdf = pdf(doc);
    const blob = await asPdf.toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Orcamento_${budgetData.number || budgetData.uuid}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("[downloadBudgetPdfV2] Erro ao gerar PDF:", error);
    throw error;
  }
};
